/**
 * A aba Sessões (docs/spec/session-clock.md, "A aba Sessões"): a linha do tempo de uma sessão (jogo e
 * pausas), quem esteve nela e os rótulos de dia e horário. Puro: quem desenha passa a hora do
 * servidor, o `t` do idioma e os formatos.
 */
import type { GameSessionEventView, GameSessionPresenceView, GameSessionSummary, RoomRole } from '../../shared.js';
import { LOCAL_FORMATS, formatDuration, type ClockFormats, type Translate } from './clockModel.js';

const MIN = 60_000;
const HOUR = 60 * MIN;

/**
 * Os formatos do relógio, mais o dia da semana (0 = domingo), o dia do mês e o mês (0 = janeiro) e o
 * começo da hora, no fuso de quem vê.
 */
export type SessionFormats = ClockFormats & { weekday: (ms: number) => number; date: (ms: number) => { day: number; month: number }; hourStart: (ms: number) => number };

export const LOCAL_SESSION_FORMATS: SessionFormats = {
  ...LOCAL_FORMATS,
  weekday: (ms) => new Date(ms).getDay(),
  date: (ms) => {
    const date = new Date(ms);
    return { day: date.getDate(), month: date.getMonth() };
  },
  hourStart: (ms) => {
    const date = new Date(ms);
    date.setMinutes(0, 0, 0);
    return date.getTime();
  },
};

/** Onde a sessão termina na linha do tempo: o fim efetivo, ou agora (a aberta). */
function endOf(session: GameSessionSummary, now: number): number {
  return session.status === 'ended' ? (session.endedAt ?? session.since) : Math.max(now, session.since);
}

/** Um trecho da linha do tempo, em fração da largura (0 a 1); `open`: ainda correndo. */
export type TimelineSegment = { kind: 'play' | 'pause'; from: number; to: number; open: boolean };
/** Uma marca embaixo da linha do tempo; `align`: o começo encosta à esquerda, o fim à direita. */
/** Uma marca embaixo da linha; `near` é a hora perto das pontas, que some numa linha estreita. */
export type TimelineTick = { pos: number; label: string; align: 'start' | 'middle' | 'end'; near: boolean };
export type Timeline = { segments: TimelineSegment[]; ticks: TimelineTick[]; now: number | null };

/**
 * A linha do tempo de uma sessão: o jogo e as pausas, pelos passos dela. A encerrada ocupa a largura
 * toda; a aberta vai crescendo, numa escala do maior entre 4 h e o tempo corrido mais 15 min. As
 * marcas são o começo, as horas cheias (de 2 em 2 h passando de 8 h, de 4 em 4 passando de 16) e, na
 * encerrada, o fim.
 */
export function timeline(session: GameSessionSummary, events: GameSessionEventView[], now: number, formats: SessionFormats = LOCAL_SESSION_FORMATS): Timeline {
  const start = session.startedAt;
  const end = endOf(session, now);
  const open = session.status !== 'ended';
  const span = open ? Math.max(4 * HOUR, end - start + 15 * MIN) : Math.max(end - start, 1);
  const pos = (at: number) => Math.min(1, Math.max(0, (at - start) / span));
  const segments: TimelineSegment[] = [];
  let current: { kind: 'play' | 'pause'; from: number } | null = events.length ? null : { kind: 'play', from: start };
  for (const event of events) {
    const at = Math.min(Math.max(event.at, start), end);
    if (current && at > current.from) segments.push({ kind: current.kind, from: pos(current.from), to: pos(at), open: false });
    current = event.kind === 'end' ? null : { kind: event.kind === 'pause' ? 'pause' : 'play', from: at };
  }
  if (current && end > current.from) segments.push({ kind: current.kind, from: pos(current.from), to: pos(end), open });

  const ticks: TimelineTick[] = [{ pos: 0, label: formats.time(start), align: 'start', near: false }];
  const every = span > 16 * HOUR ? 4 : span > 8 * HOUR ? 2 : 1;
  // longe do começo e do fim, para os rótulos não se encostarem; numa linha estreita, ainda mais longe
  const last = open ? 0.96 : 0.86;
  for (let at = formats.hourStart(start) + HOUR, i = 0; at < start + span; at += HOUR, i += 1) {
    const p = pos(at);
    if (i % every === 0 && p > 0.1 && p < last) ticks.push({ pos: p, label: formats.time(at), align: 'middle', near: p < 0.2 || p > (open ? 0.92 : 0.8) });
  }
  if (!open && end - start >= MIN) ticks.push({ pos: 1, label: formats.time(end), align: 'end', near: false });
  return { segments, ticks, now: open ? pos(end) : null };
}

/** Uma pessoa na sessão: os trechos dela (cortados pela sessão), o papel e o tempo somado. */
export type SessionPerson = {
  key: string;
  name: string;
  role: RoomRole;
  /** está na mesa agora, com a sessão aberta */
  present: boolean;
  spans: { from: number; to: number }[];
  totalMs: number;
};

/**
 * Quem esteve na sessão, uma vez por pessoa, na ordem em que chegou. Cada trecho é cortado pelo
 * começo e pelo fim da sessão; o papel é o da entrada mais recente.
 */
export function people(presence: GameSessionPresenceView[], session: GameSessionSummary, now: number): SessionPerson[] {
  const start = session.startedAt;
  const end = endOf(session, now);
  const open = session.status !== 'ended';
  const byKey = new Map<string, SessionPerson>();
  for (const entry of presence) {
    const key = entry.userId ?? `name:${entry.userName}`;
    const from = Math.max(entry.joinedAt, start);
    const to = Math.min(entry.leftAt ?? end, end);
    let person = byKey.get(key);
    if (!person) byKey.set(key, (person = { key, name: entry.userName, role: entry.role, present: false, spans: [], totalMs: 0 }));
    person.name = entry.userName;
    person.role = entry.role;
    if (open && entry.leftAt === null) person.present = true;
    if (to >= from) {
      person.spans.push({ from, to });
      person.totalMs += to - from;
    }
  }
  return [...byKey.values()].filter((person) => person.spans.length || person.present);
}

/**
 * O que aparece ao lado do nome: "desde 19:05" (está na mesa agora, desde a última entrada) ou quanto
 * tempo a pessoa ficou na sessão (os trechos ficam na dica).
 */
export function personText(t: Translate, person: SessionPerson, formats: ClockFormats = LOCAL_FORMATS): string {
  const last = person.spans[person.spans.length - 1];
  if (person.present && last) return t('sessions.person.since', { time: formats.time(last.from) });
  return formatDuration(t, person.totalMs);
}

/** Os trechos de uma pessoa, para a dica: "19:05–20:10, 20:30–22:31". */
export function personSpans(person: SessionPerson, formats: ClockFormats = LOCAL_FORMATS): string {
  return person.spans.map((span) => `${formats.time(span.from)}–${formats.time(span.to)}`).join(', ');
}

/** "quinta, 29/10" (`long`) ou "qui 22/10". */
export function dayLabel(t: Translate, at: number, long: boolean, formats: SessionFormats = LOCAL_SESSION_FORMATS): string {
  const name = t(`sessions.weekday.${long ? 'long' : 'short'}.${formats.weekday(at)}`);
  return t(long ? 'sessions.day.long' : 'sessions.day.short', { weekday: name, day: formats.day(at) });
}

/** O bloco de data da lista: o dia da semana, o dia do mês e o mês ("SEX", 30, "OUT"). */
export function dateBlock(t: Translate, at: number, formats: SessionFormats = LOCAL_SESSION_FORMATS): { weekday: string; day: number; month: string } {
  const { day, month } = formats.date(at);
  return { weekday: t(`sessions.weekday.short.${formats.weekday(at)}`), day, month: t(`sessions.month.short.${month}`) };
}

/**
 * A linha de baixo de uma sessão na lista, em pedaços (a linha quebra entre eles, nunca no meio): o
 * horário e as pausas; com título, também o número (sem título, o número já é o nome dela).
 */
export function sessionFacts(t: Translate, session: GameSessionSummary, now: number, formats: ClockFormats = LOCAL_FORMATS): string[] {
  return [
    ...(session.title ? [t('sessions.untitled', { n: session.number })] : []),
    timeRange(t, session, now, formats),
    session.pausedMs ? t('sessions.row.paused', { paused: formatDuration(t, session.pausedMs) }) : t('sessions.row.noPause'),
  ];
}

/** "Última: sexta, 30/10 · 56 min de jogo" (no alto da aba, sem sessão aberta). */
export function lastSessionText(t: Translate, session: GameSessionSummary, formats: SessionFormats = LOCAL_SESSION_FORMATS): string {
  return t('sessions.last', { day: dayLabel(t, session.startedAt, true, formats), played: formatDuration(t, session.playedMs) });
}

/** "19:00–22:31"; passando da meia-noite, com o dia do fim. */
export function timeRange(t: Translate, session: GameSessionSummary, now: number, formats: ClockFormats = LOCAL_FORMATS): string {
  const end = endOf(session, now);
  const from = formats.time(session.startedAt);
  const to = formats.time(end);
  return formats.day(end) === formats.day(session.startedAt) ? `${from}–${to}` : t('sessions.range.nextDay', { from, to, day: formats.day(end) });
}

/** As sessões que já terminaram (a aberta fica no alto da aba), da mais nova para a mais velha. */
export function pastSessions(sessions: GameSessionSummary[]): GameSessionSummary[] {
  return sessions.filter((session) => session.status === 'ended');
}

/** Junta a página nova à lista, sem repetir sessão (a lista pode ter mudado entre as duas). */
export function mergePage(list: GameSessionSummary[], page: GameSessionSummary[]): GameSessionSummary[] {
  const known = new Set(list.map((session) => session.number));
  return [...list, ...page.filter((session) => !known.has(session.number))];
}
